import { useEffect, useRef, useState } from 'react';
import { apiClient, ApiClientError } from '../lib/api';
import { detectCagnotteProvider } from '../lib/cagnotte';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement | string,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          theme?: string;
          size?: 'normal' | 'compact' | 'invisible';
        },
      ) => string;
      reset: (id?: string) => void;
    };
    onloadTurnstileCallback?: () => void;
  }
}

const TURNSTILE_SITE_KEY =
  // Cloudflare's public test sitekey (always passes); replace via env in production build
  (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_TURNSTILE_SITE_KEY ??
  '1x00000000000000000000AA';

/**
 * Test sitekeys Cloudflare commencent par `1x` ou `2x` → on n'affiche pas
 * le widget. Le worker bypass cote serveur, donc la validation passe sans
 * token reel. Production sitekeys → render avec size: 'invisible'.
 */
const IS_TEST_SITEKEY =
  TURNSTILE_SITE_KEY.startsWith('1x') || TURNSTILE_SITE_KEY.startsWith('2x');

type Status = 'idle' | 'submitting' | 'sent' | 'error';

const ACCEPTED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_INPUT_PHOTO_BYTES = 30 * 1024 * 1024; // 30 MB input — sera resize/compressé côté client
const MAX_OUTPUT_BASE64_LENGTH = 13_000_000; // 13 MB data URL ≈ 9.5 MB binaire (limite worker 10 MB)
const RESIZE_DIMS = [3840, 2560, 2048, 1600, 1200]; // 4K → 1200px (préserve plus de détail avant compress)
const AVIF_QUALITIES = [60, 50, 40, 30, 22]; // 60 ≈ JPEG q85 visuel, AVIF 50% < JPEG en poids
const JPEG_FALLBACK_QUALITIES = [0.88, 0.78, 0.68, 0.55];

/**
 * Convertit un ArrayBuffer en data URL base64 sans empiler des charCodes
 * au-dessus de la limite stack (chunks 32 KB).
 */
function arrayBufferToDataUrl(buf: ArrayBuffer | Uint8Array, mime: string): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunk)));
  }
  return `data:${mime};base64,${btoa(bin)}`;
}

/**
 * Tentative d'encode AVIF via @jsquash/avif (lazy-loaded WASM).
 * Retourne null si WASM indisponible / échec encode. Le dynamic import
 * est mis en cache par le bundler après le premier appel.
 */
async function tryEncodeAvif(imageData: ImageData, quality: number): Promise<ArrayBuffer | null> {
  try {
    const { encode } = await import('@jsquash/avif');
    return await encode(imageData, { quality, speed: 6 });
  } catch {
    return null;
  }
}

/**
 * Resize + compresse une image pour fit sous MAX_OUTPUT_BASE64_LENGTH.
 * - Encode en AVIF (50% plus efficace que JPEG à qualité visuelle équivalente).
 * - Itère sur les dimensions (2400 → 900) et qualités (60 → 22) jusqu'à passer.
 * - Si AVIF indisponible (browser ancien ou WASM en échec) → fallback JPEG.
 * - WASM AVIF lazy-load (~500 KB gzipped, jamais chargé tant que l'user n'attache pas de photo).
 */
async function resizePhoto(file: File): Promise<{ dataUrl: string; sizeKb: number; mime: string }> {
  const objectUrl = URL.createObjectURL(file);
  const img = new Image();
  try {
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('lecture image impossible'));
      img.src = objectUrl;
    });

    let avifAvailable = true;
    for (const maxDim of RESIZE_DIMS) {
      const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
      const w = Math.round(img.naturalWidth * scale);
      const h = Math.round(img.naturalHeight * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('canvas indisponible');
      ctx.drawImage(img, 0, 0, w, h);

      // 1. Tentative AVIF (qualité visuelle nettement supérieure à poids égal)
      if (avifAvailable) {
        const imageData = ctx.getImageData(0, 0, w, h);
        for (const q of AVIF_QUALITIES) {
          const buf = await tryEncodeAvif(imageData, q);
          if (!buf) {
            avifAvailable = false;
            break;
          }
          const dataUrl = arrayBufferToDataUrl(buf, 'image/avif');
          if (dataUrl.length <= MAX_OUTPUT_BASE64_LENGTH) {
            return { dataUrl, sizeKb: Math.round(buf.byteLength / 1024), mime: 'image/avif' };
          }
        }
      }

      // 2. Fallback JPEG (toDataURL natif canvas)
      for (const q of JPEG_FALLBACK_QUALITIES) {
        const dataUrl = canvas.toDataURL('image/jpeg', q);
        if (dataUrl.length <= MAX_OUTPUT_BASE64_LENGTH) {
          const b64 = dataUrl.split(',')[1] ?? '';
          const bytes = Math.round((b64.length * 3) / 4);
          return { dataUrl, sizeKb: Math.round(bytes / 1024), mime: 'image/jpeg' };
        }
      }
    }
    throw new Error('photo impossible à compresser sous 750 KB');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function Send() {
  useEffect(() => {
    document.body.dataset.route = '/send';
    return () => {
      document.body.dataset.route = '';
    };
  }, []);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [giftUrl, setGiftUrl] = useState('');
  const [giftLabel, setGiftLabel] = useState('');
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const [photoMeta, setPhotoMeta] = useState<{ name: string; sizeKb: number; mime: string } | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [photoEncoding, setPhotoEncoding] = useState(false);
  const [website, setWebsite] = useState(''); // honeypot
  const [turnstileToken, setTurnstileToken] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  // Render Turnstile widget — skip render en mode test (worker bypass)
  useEffect(() => {
    if (IS_TEST_SITEKEY) {
      setTurnstileToken('test-bypass');
      return;
    }
    const renderWidget = () => {
      if (!window.turnstile || !turnstileRef.current) return;
      window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => setTurnstileToken(token),
        theme: 'light',
        size: 'invisible',
      });
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback';
    script.async = true;
    script.defer = true;
    window.onloadTurnstileCallback = renderWidget;
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
      window.onloadTurnstileCallback = undefined;
    };
  }, []);

  const provider = giftUrl.trim() ? detectCagnotteProvider(giftUrl.trim()) : null;
  const titleOk = title.trim().length > 0 && title.length <= 80;
  const bodyOk = body.trim().length > 0 && body.length <= 10000;
  const formOk = titleOk && bodyOk && status !== 'submitting' && !photoEncoding;

  async function onPhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // permettre de re-selectionner le meme fichier apres clear
    if (!file) return;
    setPhotoError(null);
    if (!ACCEPTED_PHOTO_TYPES.includes(file.type)) {
      setPhotoError('format non supporte (jpeg/png/webp uniquement)');
      return;
    }
    if (file.size > MAX_INPUT_PHOTO_BYTES) {
      setPhotoError(`photo trop lourde (${(file.size / 1024 / 1024).toFixed(1)} MB > 30 MB)`);
      return;
    }
    setPhotoEncoding(true);
    try {
      const { dataUrl, sizeKb, mime } = await resizePhoto(file);
      setPhotoDataUrl(dataUrl);
      setPhotoMeta({ name: file.name, sizeKb, mime });
    } catch (err) {
      setPhotoError(err instanceof Error ? err.message : 'erreur compression photo');
    } finally {
      setPhotoEncoding(false);
    }
  }

  function clearPhoto() {
    setPhotoDataUrl(null);
    setPhotoMeta(null);
    setPhotoError(null);
    if (photoInputRef.current) photoInputRef.current.value = '';
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formOk) return;
    setStatus('submitting');
    setError(null);
    try {
      await apiClient.submitMessage({
        title: title.trim(),
        body: body.trim(),
        giftUrl: giftUrl.trim() || null,
        giftLabel: giftLabel.trim() || null,
        photoDataUrl: photoDataUrl,
        website,
        turnstileToken,
      });
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError(err instanceof ApiClientError ? err.message : 'erreur réseau');
    }
  }

  if (status === 'sent') {
    return (
      <main className="m-grid-center" style={{ minHeight: '100vh', padding: 'var(--sp-6)' }}>
        <section style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
          <p className="m-label" style={{ marginBottom: 12 }}>200 / ok</p>
          <h1 style={{ fontSize: 24, fontWeight: 400, margin: '0 0 16px' }}>message envoyé</h1>
          <p style={{ color: 'var(--fg-muted)', marginBottom: 24 }}>
            publié dans le terminal de marthe.
          </p>
          <a className="m-btn" href="/">retour accueil</a>
        </section>
      </main>
    );
  }

  return (
    <main style={{ minHeight: '100vh', padding: 'var(--sp-6)' }}>
      <section style={{ maxWidth: 480, margin: '0 auto' }}>
        <header style={{ marginBottom: 'var(--sp-8)' }}>
          <p className="m-label">marthe / send</p>
          <h1 style={{ fontSize: 20, fontWeight: 400, margin: '8px 0 4px' }}>
            écrire un mot
          </h1>
          <p style={{ color: 'var(--fg-muted)', fontSize: 12 }}>
            publication immédiate. mono uniquement, pas de markdown.
          </p>
        </header>

        <form className="m-form" onSubmit={onSubmit} noValidate>
          <div className="m-field">
            <label className="m-label" htmlFor="title">titre</label>
            <input
              id="title"
              className="m-input"
              type="text"
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              autoComplete="off"
            />
            <span className="m-field__hint">{title.length}/80</span>
          </div>

          <div className="m-field">
            <label className="m-label" htmlFor="body">message</label>
            <textarea
              id="body"
              className="m-textarea"
              maxLength={10000}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
            />
            <span className="m-field__hint">{body.length}/10000</span>
          </div>

          {/* Photo upload (optionnel) — 2 MB max, jpeg/png/webp */}
          <div className="m-field">
            <label className="m-label" htmlFor="photo">photo (optionnel)</label>
            <input
              ref={photoInputRef}
              id="photo"
              className="m-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={onPhotoChange}
              data-testid="photo-input"
            />
            <span className="m-field__hint">
              jpeg / png / webp · 30 MB max · compression AVIF auto · sera convertie en glyph art
            </span>
            {photoEncoding && (
              <p
                style={{
                  marginTop: 8,
                  color: 'var(--fg-muted)',
                  fontSize: 11,
                  border: '1px dashed var(--border)',
                  padding: 6,
                  fontStyle: 'italic',
                }}
                data-testid="photo-encoding"
              >
                compression en cours…
              </p>
            )}
            {photoError && (
              <p
                style={{
                  marginTop: 8,
                  color: 'var(--ink)',
                  fontSize: 11,
                  border: '1px solid var(--ink)',
                  padding: 6,
                }}
                data-testid="photo-error"
              >
                {photoError}
              </p>
            )}
            {photoDataUrl && (
              <div
                style={{
                  marginTop: 12,
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                  border: '1px solid var(--border)',
                  padding: 8,
                }}
                data-testid="photo-preview"
              >
                <img
                  src={photoDataUrl}
                  alt="aperçu"
                  style={{
                    width: 96,
                    height: 96,
                    objectFit: 'cover',
                    border: '1px solid var(--border)',
                  }}
                />
                <div style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)', minWidth: 0 }}>
                  <div
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {photoMeta?.name ?? 'photo'}
                  </div>
                  <div style={{ marginTop: 2 }}>{photoMeta?.sizeKb ?? '?'} kB</div>
                  <button
                    type="button"
                    onClick={clearPhoto}
                    data-testid="photo-clear"
                    style={{
                      marginTop: 8,
                      background: 'transparent',
                      border: 0,
                      padding: 0,
                      fontFamily: 'inherit',
                      fontSize: 11,
                      letterSpacing: '0.06em',
                      color: 'var(--fg-muted)',
                      cursor: 'pointer',
                      borderBottom: '1px solid var(--border)',
                    }}
                  >
                    × retirer
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="m-field">
            <label className="m-label" htmlFor="giftUrl">url cagnotte (optionnel)</label>
            <input
              id="giftUrl"
              className="m-input"
              type="url"
              value={giftUrl}
              onChange={(e) => setGiftUrl(e.target.value)}
              autoComplete="off"
              placeholder="https://..."
            />
            {provider && (
              <span className="m-field__hint">détecté : {provider}</span>
            )}
          </div>

          <div className="m-field">
            <label className="m-label" htmlFor="giftLabel">étiquette cagnotte (optionnel)</label>
            <input
              id="giftLabel"
              className="m-input"
              type="text"
              maxLength={40}
              value={giftLabel}
              onChange={(e) => setGiftLabel(e.target.value)}
              autoComplete="off"
            />
          </div>

          {/* Honeypot — must remain empty */}
          <div className="m-honeypot" aria-hidden="true">
            <label htmlFor="website">website</label>
            <input
              id="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>

          <div ref={turnstileRef} data-testid="turnstile-mount" />

          {error && (
            <p style={{ color: 'var(--ink)', fontSize: 12, border: '1px solid var(--ink)', padding: 8 }}>
              {error}
            </p>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="m-btn" type="submit" disabled={!formOk}>
              {status === 'submitting' ? 'envoi…' : 'envoyer'}
            </button>
            <a className="m-btn m-btn--ghost" href="/">annuler</a>
          </div>
        </form>
      </section>
    </main>
  );
}
